import React, { useState } from "react"
import { useQuery } from "@apollo/client/react"
import { GetCustomerReservationsDocument } from "@/graphql/graphql"
import { DateTime } from "luxon"
import Pagination from "@/components/pagination"
import { RESERVATION_COLORS, STATUS_LABELS, ReservationStatus } from "@/constants/reservations"
import Skeleton from "@mui/material/Skeleton"
import { Calendar, Clock, MapPin, Users, Armchair } from "lucide-mui"
import { formatTableName } from "@/shared/tables"
import i18n from "@/i18n"

const ReservationSkeleton = () => (
  <div className="flex items-center space-x-4 px-6 py-3 border-b border-gray-300">
    <Skeleton variant="rectangular" width={48} height={48} className="rounded" />
    <div className="flex-1">
      <Skeleton variant="text" width="60%" />
    </div>
    <Skeleton variant="rectangular" width={80} height={24} className="rounded-full" />
  </div>
)

const StatusBadge = ({ status }: { status: ReservationStatus }) => {
  const colors = RESERVATION_COLORS[status] || RESERVATION_COLORS.removed
  const label = STATUS_LABELS[status] || status

  return (
    <div
      className="flex items-center px-3 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap"
      style={{ backgroundColor: colors.main }}
    >
      <span className="w-2 h-2 rounded-full bg-white mr-2 flex-shrink-0" />
      {label}
    </div>
  )
}

const ReservationItem = ({ reservation }) => {
  const startTime = DateTime.fromISO(reservation.startTime)
  const endTime = reservation.endTime ? DateTime.fromISO(reservation.endTime) : null
  const timeRange = endTime
    ? `${startTime.toLocaleString(DateTime.TIME_SIMPLE)} - ${endTime.toLocaleString(DateTime.TIME_SIMPLE)}`
    : startTime.toLocaleString(DateTime.TIME_SIMPLE)

  return (
    <div
      data-testid={`panel-clients-modal-reservations-item-${reservation.id}`}
      className="flex items-center px-6 py-3 border-0 border-b border-solid border-gray-300 hover:bg-gray-50 gap-4"
    >
      <div className="flex-shrink-0 w-12 text-center">
        <div className="text-xl font-bold text-gray-700">{startTime.day}</div>
        <div className="text-xs text-gray-500 uppercase">{startTime.monthShort}</div>
      </div>

      <div className="flex-1 bg-gray-100 rounded-lg px-3 py-2">
        <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Users className="text-gray-400 text-sm" />
            <span>{reservation.peopleAmount}</span>
          </div>
          {reservation.venue && (
            <div className="flex items-center gap-1">
              <MapPin className="text-gray-400 text-sm" />
              <span>{reservation.venue.name}</span>
            </div>
          )}
          {reservation.table && (
            <div className="flex items-center gap-1">
              <Armchair className="text-gray-400 text-sm" />
              <span>{formatTableName(reservation.table, { includeGroupName: false })}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="text-gray-400 text-sm" />
            <span>{timeRange}</span>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0">
        <StatusBadge status={reservation.status} />
      </div>
    </div>
  )
}

const ReservationsTab = ({ customer, customerContact: _customerContact }) => {
  const [page, setPage] = useState(1)
  const perPage = 5

  const { data, loading, error } = useQuery(GetCustomerReservationsDocument, {
    variables: {
      id: customer.id,
      page,
      perPage,
    },
  })

  const metadata = data?.business?.customer?.reservations?.metadata
  const reservations = data?.business?.customer?.reservations?.collection || []

  if (!data && loading) {
    return (
      <div className="flex flex-col">
        {Array.from({ length: 5 }).map((_, index) => (
          <ReservationSkeleton key={index} />
        ))}
      </div>
    )
  }

  if (error) return <div className="p-6 text-red-600">Error: {error.message}</div>

  if (reservations.length === 0) {
    return (
      <div
        data-testid="panel-clients-modal-reservations-empty"
        className="flex flex-col items-center justify-center py-12 text-gray-500"
      >
        <Calendar className="text-6xl mb-3" />
        <div>{i18n.t("app.clients.client_modal.reservations.empty")}</div>
      </div>
    )
  }

  const now = DateTime.now()
  const upcoming: typeof reservations = []
  const previous: typeof reservations = []

  reservations.forEach((reservation) => {
    const startTime = DateTime.fromISO(reservation.startTime)
    if (startTime >= now) {
      upcoming.push(reservation)
    } else {
      previous.push(reservation)
    }
  })

  return (
    <div data-testid="panel-clients-modal-reservations-container" className="flex flex-col py-4">
      {upcoming.length > 0 && (
        <>
          <div className="px-6 py-2 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase">
              {i18n.t("app.clients.client_modal.reservations.upcoming")}
            </h3>
          </div>
          {upcoming.map((reservation) => (
            <ReservationItem key={reservation.id} reservation={reservation} />
          ))}
        </>
      )}

      {previous.length > 0 && (
        <>
          <div className="px-6 py-2 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase">
              {i18n.t("app.clients.client_modal.reservations.previous")}
            </h3>
          </div>
          {previous.map((reservation) => (
            <ReservationItem key={reservation.id} reservation={reservation} />
          ))}
        </>
      )}

      {metadata && metadata.totalPages > 1 && (
        <div data-testid="panel-clients-modal-reservations-pagination" className="flex justify-center py-4">
          <Pagination metadata={metadata} page={page} onPageChange={setPage} />
        </div>
      )}
    </div>
  )
}

export default ReservationsTab
